import { withTranslation } from 'react-i18next';

import DataTable from '../../../../components/Datatable';
import { getSortKeys, setAdditionalStyles } from '../../../../helpers/table';
import { formatPeriod, searchTypeLabel } from '../../GscTools/period';
import { MATCH_POLICY } from '../brand';
import { SCOPE_COLUMNS } from '../keys';

/**
 * Block 05 — what this report was read under, and the export of exactly that.
 *
 * It exists because this page's numbers are not reproducible without its rule. Two people can run
 * the same property over the same dates and get different brand totals, legitimately, and the
 * only thing that explains the difference is the expression listed here — so the expression
 * travels with the figures, on screen and into the file.
 *
 * Three rows are statements about what this report does not know, and they are rows rather than
 * footnotes so they survive into the export: the share's denominator is the classified clicks and
 * not the property's traffic; the queries Search Console withheld are not counted, estimated or
 * assigned to a side; and nothing here is stored once the tab is closed.
 */
function BrandCoverage({ t, scope, evidencePeriod, onExport, exportable }) {
    const columns = SCOPE_COLUMNS(t);
    const literal = scope.policy === MATCH_POLICY.LITERAL;

    const rows = [
        {
            id: 'classifier',
            item: t('tools.brand_non_brand.coverage.classifier'),
            value: t('tools.brand_non_brand.coverage.classifier_value'),
        },
        {
            id: 'policy',
            item: t('tools.brand_non_brand.coverage.policy'),
            value: t(`tools.brand_non_brand.policies.${scope.policy}`),
        },
        {
            id: 'terms',
            item: t('tools.brand_non_brand.coverage.terms'),
            value: literal
                ? scope.terms.map((term) => term.match).join(' · ')
                : t('tools.brand_non_brand.coverage.terms_regex'),
        },
        {
            id: 'pattern',
            item: t('tools.brand_non_brand.coverage.pattern'),
            value: scope.compiledPattern,
        },
        {
            id: 'brand_filter',
            item: t('tools.brand_non_brand.coverage.brand_filter'),
            value: 'query includingRegex',
        },
        {
            id: 'non_brand_filter',
            item: t('tools.brand_non_brand.coverage.non_brand_filter'),
            value: 'query excludingRegex',
        },
        {
            id: 'source',
            item: t('tools.brand_non_brand.coverage.source'),
            value: searchTypeLabel(scope.searchType, t),
        },
        {
            id: 'country',
            item: t('tools.gsc.controls.country'),
            value: scope.country?.toUpperCase() || t('tools.gsc.controls.all_countries'),
        },
        {
            id: 'device',
            item: t('tools.gsc.controls.device'),
            value:
                (scope.device && t(`tools.gsc.devices.${scope.device.toLowerCase()}`)) ||
                t('tools.gsc.controls.all_devices'),
        },
        {
            id: 'evidence_window',
            item: t('tools.brand_non_brand.coverage.evidence_window'),
            value: formatPeriod(evidencePeriod),
        },
        {
            id: 'denominator',
            item: t('tools.brand_non_brand.coverage.denominator'),
            value: t('tools.brand_non_brand.coverage.denominator_value'),
        },
        {
            id: 'unknown',
            item: t('tools.brand_non_brand.coverage.unknown'),
            value: t('tools.brand_non_brand.coverage.unknown_value'),
        },
        {
            id: 'retention',
            item: t('tools.brand_non_brand.coverage.retention'),
            value: t('tools.brand_non_brand.coverage.retention_value'),
        },
    ];

    return (
        <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
            <div className="card-header d-flex justify-content-between">
                <div>
                    <p className="card-title">{t('tools.brand_non_brand.coverage.title')}</p>
                    <p className="gsc-tool-sub">
                        {t('tools.brand_non_brand.coverage.description')}
                    </p>
                </div>

                <div className="gsc-tool-icon-actions">
                    <button
                        type="button"
                        title={t('tools.brand_non_brand.coverage.export')}
                        aria-label={t('tools.brand_non_brand.coverage.export')}
                        onClick={onExport}
                        disabled={!exportable}
                    >
                        <i className="icon-fi_download" />
                    </button>
                </div>
            </div>

            <div className="card-body">
                <DataTable
                    nodes={rows}
                    COLUMNS={columns}
                    sortFns={getSortKeys(columns)}
                    additionalStyles={setAdditionalStyles(columns)}
                    onlyTable
                    notUseDefaultStyle
                    customContainerStyles="gsc-tool-rules-table"
                />

                <p className="gsc-tool-table-note">
                    {t('tools.brand_non_brand.coverage.export_note')}
                </p>
                <p className="gsc-tool-table-note">
                    {t('tools.brand_non_brand.coverage.evidence_export_note')}
                </p>
            </div>
        </div>
    );
}

export default withTranslation()(BrandCoverage);
